import type { InfiniteData } from '@tanstack/react-query';

import type { GetInquiriesV2Response, InquiryV2 } from '@/services/getInquiriesV2';

type ShouldMarkRead = (conversationId: string, hasUnread: boolean | undefined) => boolean;

/**
 * Clears unread on every copy of a thread a loaded inbox list holds.
 *
 * A grouped list keeps the threads it gathered into question cards out of `inquiries`, in each
 * group's original, its submitter threads and its relay rows. Clearing only `inquiries` left a
 * card's row dot lit after the thread was read, while the tab count, which is refetched, had
 * already dropped. Every place a group holds a thread has to be cleared here, or that copy drifts.
 */
export function clearInboxUnread(
  data: InfiniteData<GetInquiriesV2Response> | undefined,
  shouldMarkRead: ShouldMarkRead
): InfiniteData<GetInquiriesV2Response> | undefined {
  if (!data?.pages) {
    return data;
  }

  const clear = (inquiry: InquiryV2) =>
    shouldMarkRead(inquiry.conversationId, inquiry.hasUnread)
      ? { ...inquiry, hasUnread: false }
      : inquiry;

  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      inquiries: page.inquiries?.map(clear),
      ...(page.questionGroups && {
        questionGroups: page.questionGroups.map((group) => ({
          ...group,
          original: clear(group.original),
          originalGroup: group.originalGroup.map(clear),
          threads: group.threads.map(clear),
        })),
      }),
    })),
  };
}
